// @ts-check

/**
 * Pairing prompt — asks a yes/no question when the cart ends up holding two
 * products that match a configured pair of type/tag rules, and records the
 * answer as a cart attribute so it reaches the order.
 *
 * The match itself is not made here. Product tags are not exposed by the cart
 * AJAX API, so the rules are evaluated in Liquid by
 * `snippets/pairing-prompt-state.liquid`, which is rendered inside the cart
 * sections. Add-to-cart already re-renders those sections and hands them back
 * on the event promise, so on the common path this component reads the result
 * out of a response the theme was fetching anyway and makes no request of its
 * own. Answering is the only network call it initiates.
 *
 * Cart attribute contract (ops, keep stable): the merchant-set attribute name
 * for the rule, with a value of exactly `yes` or `no`.
 */

import { Component } from '@theme/component';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

/**
 * Reads the marker emitted by `snippets/pairing-prompt-state.liquid`. That
 * snippet owns the tag name and puts `data-rule` first, so a string scan is
 * enough — parsing an entire re-rendered cart section into a document to read
 * one attribute is wasted work on the add-to-cart path.
 */
const STATE_PATTERN = /<pairing-prompt-state\s+data-rule="([^"]*)"\s+data-stale="([^"]*)"/;

/** @param {string} rule */
const dismissalKey = (rule) => `pairing-prompt:dismissed:${rule}`;

/**
 * Rule ids are the merchant's rule slot numbers. Anything else came from
 * somewhere it shouldn't have, and would go straight into a selector.
 *
 * @param {string | undefined} rule
 * @returns {string | null}
 */
const asRuleId = (rule) => (rule && /^[0-9]+$/.test(rule) ? rule : null);

/**
 * Attributes this page has already blanked. The cart section isn't re-rendered
 * for an attribute write, so the marker keeps reporting them as stale until
 * something else re-renders it — without this we'd re-POST on every cart update.
 * Writing a real answer re-arms the attribute.
 *
 * @type {Set<string>}
 */
const clearedAttributes = new Set();

/**
 * Writes cart attributes. Cart attributes don't affect anything the cart
 * renders, so there's nothing to re-render — the next natural section render
 * picks the new values up. An empty string removes an attribute.
 *
 * @param {Record<string, string>} attributes
 */
function saveAttributes(attributes) {
  for (const [attribute, value] of Object.entries(attributes)) {
    if (value) clearedAttributes.delete(attribute);
    else clearedAttributes.add(attribute);
  }

  return fetch(Theme.routes.cart_update_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes }),
  }).catch((error) => console.error('[pairing-prompt] Failed to write cart attributes:', error));
}

/**
 * @typedef {object} PairingPromptRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends DialogComponent
 */
class PairingPromptComponent extends DialogComponent {
  #abortController = new AbortController();

  /**
   * The rule currently on screen and still unanswered. Cleared the moment an
   * answer is given, which is how `#onDialogClose` tells "answered" apart from
   * "dismissed".
   *
   * @type {string | null}
   */
  #activeRule = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Records the customer's answer. Wired up from the markup as
   * `on:click="/answer/yes"`.
   *
   * @param {string} value - `yes` or `no`.
   */
  answer = (value) => {
    const rule = this.#activeRule;

    // Clear first: this is what marks the imminent close as answered.
    this.#activeRule = null;
    this.closeDialog();

    const attribute = rule ? this.#panel(rule)?.dataset.attribute : null;
    if (!attribute) return;

    // The cart isn't re-rendered for an attribute write, so bring the tick box
    // into line directly. `rule` is digits-only (asRuleId), safe to interpolate.
    const toggle = document.querySelector(`pairing-prompt-toggle-component[data-rule="${rule}"] input`);
    if (toggle instanceof HTMLInputElement) toggle.checked = value === 'yes';

    saveAttributes({ [attribute]: value === 'yes' ? 'yes' : 'no' });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    const isAdd = event.action === 'add';

    event.promise
      ?.then(async ({ detail }) => {
        if (detail?.didError) return;

        // Removals and quantity changes can strand an answer, so the state is
        // read on every action — but only an add is allowed to fall back to a
        // request of its own, and only an add can open the dialog.
        const state = await this.#resolveState(detail?.sections, isAdd);
        if (!state) return;

        this.#clearStale(state.stale);

        if (!isAdd || this.refs.dialog.open) return;

        const rule = asRuleId(state.rule);
        if (!rule || this.#isDismissed(rule)) return;

        this.#show(rule);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pairing-prompt] Event promise rejected:', error);
      });
  };

  /**
   * Blanks the cart attributes of rules that hold an answer but no longer match,
   * so an order never carries an answer about products that were removed.
   *
   * @param {string} staleRules - Comma-separated rule ids from the marker.
   */
  #clearStale(staleRules) {
    /** @type {Record<string, string>} */
    const attributes = {};

    for (const id of staleRules.split(',')) {
      const rule = asRuleId(id.trim());
      // The attribute name comes off the dialog panel rather than the marker so
      // it arrives HTML-decoded by the parser.
      const attribute = rule && this.#panel(rule)?.dataset.attribute;

      if (attribute && !clearedAttributes.has(attribute)) attributes[attribute] = '';
    }

    if (Object.keys(attributes).length) saveAttributes(attributes);
  }

  /**
   * Closing without answering suppresses this rule for the rest of the session,
   * so a dismissed question doesn't come back on every subsequent add. An
   * answered rule doesn't need this — its cart attribute stops the server from
   * matching it again.
   */
  #onDialogClose = () => {
    const rule = this.#activeRule;
    if (!rule) return;

    this.#activeRule = null;

    try {
      sessionStorage.setItem(dismissalKey(rule), '1');
    } catch {
      // Private browsing / storage disabled — worst case the prompt reappears.
    }
  };

  /**
   * Reads the marker, preferring the cart sections that the cart mutation
   * already returned.
   *
   * @param {Record<string, string> | undefined} sections
   * @param {boolean} allowFetch - Whether a missing marker justifies a request.
   * @returns {Promise<{ rule: string, stale: string } | null>}
   */
  async #resolveState(sections, allowFetch) {
    for (const html of Object.values(sections ?? {})) {
      if (typeof html !== 'string') continue;

      const match = html.match(STATE_PATTERN);

      // The marker is authoritative: present and empty means "nothing to do",
      // which is a real answer, not a reason to go and ask again.
      if (match) return { rule: match[1], stale: match[2] };
    }

    // No cart section came back. This is the cart type "page" case, where a
    // product template has no cart section to re-render, so ask the server
    // directly for just the marker.
    const url = this.dataset.stateUrl;
    if (!allowFetch || !url) return null;

    try {
      const html = await fetch(url, { credentials: 'same-origin' }).then((response) => response.text());
      const match = html.match(STATE_PATTERN);
      return match ? { rule: match[1], stale: match[2] } : null;
    } catch (error) {
      console.warn('[pairing-prompt] Failed to fetch prompt state:', error);
      return null;
    }
  }

  /** @param {string} rule */
  #show(rule) {
    const panel = this.#panel(rule);
    if (!panel) return;

    for (const candidate of this.querySelectorAll('.pairing-prompt__panel')) {
      /** @type {HTMLElement} */ (candidate).hidden = candidate !== panel;
    }

    const heading = panel.querySelector('[id]');
    if (heading) this.refs.dialog.setAttribute('aria-labelledby', heading.id);

    this.#activeRule = rule;
    this.showDialog();
  }

  /**
   * @param {string} rule
   * @returns {HTMLElement | null}
   */
  #panel(rule) {
    return this.querySelector(`.pairing-prompt__panel[data-rule="${rule}"]`);
  }

  /** @param {string} rule */
  #isDismissed(rule) {
    try {
      return sessionStorage.getItem(dismissalKey(rule)) === '1';
    } catch {
      return false;
    }
  }
}

if (!customElements.get('pairing-prompt-component')) {
  customElements.define('pairing-prompt-component', PairingPromptComponent);
}

/**
 * The cart tick box. Lets the customer change the answer they gave the popup,
 * or give one for the first time if they dismissed it.
 *
 * Server-rendered from the cart attribute by
 * `snippets/pairing-prompt-cart-toggle.liquid`, so it is already correct on
 * arrival and stays correct through cart re-renders — this class only has to
 * write changes back.
 *
 * @typedef {object} ToggleRefs
 * @property {HTMLInputElement} checkbox - The tick box input.
 *
 * @extends Component<ToggleRefs>
 */
class PairingPromptToggleComponent extends Component {
  requiredRefs = ['checkbox'];

  /** Wired up from the markup as `on:change="/toggle"`. */
  toggle = () => {
    const { attribute } = this.dataset;
    if (!attribute) return;

    saveAttributes({ [attribute]: this.refs.checkbox.checked ? 'yes' : 'no' });
  };
}

if (!customElements.get('pairing-prompt-toggle-component')) {
  customElements.define('pairing-prompt-toggle-component', PairingPromptToggleComponent);
}
